/*
 * The four thermal palettes, as the server draws them (mushroom-bank/lambda/
 * thermal/palettes.py): Ironbow (the default), Rainbow, Grayscale and
 * High-Contrast, each a 256-entry table of [r, g, b], coldest first.
 *
 * Each table is the linear interpolation of its STOPS, rounded half up, in
 * the same arithmetic and order as palettes.py, so the two are equal entry
 * for entry; reference_data/thermal_palettes.json pins both
 * (palettes.test.js here, test_thermal_matrix.py there).
 *
 * A palette maps a temperature to a colour, never the other way (section
 * 38): the colours are for people, and every number comes from the matrix.
 * An invalid (NaN) pixel is drawn INVALID_RGB, a mid grey. Rainbow is offered
 * as asked; it is not perceptually even, which is why the legend states the
 * numbers.
 */
export const NAMES = Object.freeze(['ironbow', 'rainbow', 'grayscale', 'high_contrast']);
export const DEFAULT_PALETTE = 'ironbow';
export const INVALID_RGB = Object.freeze([128, 128, 128]);

export const STOPS = Object.freeze({
  ironbow: [[0.0, [0, 0, 0]], [0.10, [20, 0, 90]], [0.25, [100, 0, 155]], [0.40, [180, 20, 135]],
    [0.55, [230, 70, 50]], [0.70, [250, 140, 0]], [0.85, [255, 210, 40]], [1.0, [255, 255, 255]]],
  rainbow: [[0.0, [40, 0, 120]], [0.15, [0, 0, 255]], [0.35, [0, 255, 255]], [0.5, [0, 255, 0]],
    [0.65, [255, 255, 0]], [0.85, [255, 0, 0]], [1.0, [255, 255, 255]]],
  grayscale: [[0.0, [0, 0, 0]], [1.0, [255, 255, 255]]],
  high_contrast: [[0.0, [0, 0, 0]], [0.14, [0, 0, 255]], [0.28, [0, 255, 255]], [0.42, [0, 255, 0]],
    [0.57, [255, 255, 0]], [0.71, [255, 0, 0]], [0.85, [255, 0, 255]], [1.0, [255, 255, 255]]],
});

const halfUp = value => Math.floor(value + 0.5);

/** 256 [r, g, b] entries from a palette's stops (palettes.py _build). */
export function buildTable(stops) {
  const out = [];
  for (let i = 0; i < 256; i += 1) {
    const p = i / 255.0;
    for (let k = 0; k < stops.length - 1; k += 1) {
      const [p0, c0] = stops[k];
      const [p1, c1] = stops[k + 1];
      if (p0 <= p && p <= p1) {
        const f = p1 === p0 ? 0.0 : (p - p0) / (p1 - p0);
        out.push(c0.map((a, j) => halfUp(a + (c1[j] - a) * f)));
        break;
      }
    }
  }
  return out;
}

const TABLES = Object.fromEntries(NAMES.map(name => [name, Object.freeze(buildTable(STOPS[name]))]));

export function paletteTable(name) {
  if (!TABLES[name]) throw new Error(`palette is one of ${NAMES.join(', ')}`);
  return TABLES[name];
}

/** The q-th percentile (0..100) of ascending values, linear between ranks (palettes.py percentile). */
export function percentile(sorted, q) {
  if (!sorted.length) return null;
  const pos = (sorted.length - 1) * q / 100.0;
  const lo = Math.floor(pos);
  const hi = Math.min(lo + 1, sorted.length - 1);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** [lo, hi]: the 2nd to 98th percentile of the valid pixels, so a hot spot
 *  does not flatten the rest; [null, null] with none. */
export function percentileRange(temps, lo = 2.0, hi = 98.0) {
  let n = 0;
  for (let i = 0; i < temps.length; i += 1) if (!Number.isNaN(temps[i])) n += 1;
  if (!n) return [null, null];
  const values = new Float64Array(n);
  for (let i = 0, j = 0; i < temps.length; i += 1) if (!Number.isNaN(temps[i])) { values[j] = temps[i]; j += 1; }
  values.sort();
  return [percentile(values, lo), percentile(values, hi)];
}

/** A temperature's palette index, 0..255 (palettes.py indices). */
export function paletteIndex(t, lo, hi) {
  const span = hi !== null && hi !== undefined && lo !== null && lo !== undefined && hi > lo ? hi - lo : 1.0;
  const v = Math.trunc((t - lo) * (255.0 / span) + 0.5);
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/** The matrix drawn through a palette between lo and hi, as RGBA for an ImageData. */
export function renderRgba(temps, name = DEFAULT_PALETTE, lo, hi) {
  const table = paletteTable(name);
  const out = new Uint8ClampedArray(temps.length * 4);
  for (let i = 0; i < temps.length; i += 1) {
    const t = temps[i];
    const rgb = Number.isNaN(t) ? INVALID_RGB : table[paletteIndex(t, lo, hi)];
    const at = i * 4;
    out[at] = rgb[0];
    out[at + 1] = rgb[1];
    out[at + 2] = rgb[2];
    out[at + 3] = 255;
  }
  return out;
}

/** "rgb(r, g, b)" of a palette's entry. */
export const cssColour = rgb => `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
